import type { Content } from '../../perks/model/post.interface';
import type { Album, ImageType, MusicImage, MusicIndustryParty, Track } from './album.interface';

export function getAlbumId(album: Album): string | undefined {
  return album?.ids?.find((i) => i.type === 'SINGLE_INTERNAL')?.id;
}

export function getTrackId(track: Track): string | undefined {
  return track?.ids?.find((i) => i.type === 'SINGLE_INTERNAL')?.id;
}

export function getArtworkUrl(
  images: MusicImage[],
  preferredType: ImageType = 'FRONT_COVER'
): string | undefined {
  return (
    images?.find((i) => i.type === preferredType)?.url ??
    images?.find((i) => i.type === 'DEFAULT')?.url ??
    images?.[0]?.url
  );
}

/**
 * A track is playable when the backend handed us something to play. Every rule
 * about what may be played — the per-track preview toggle, the release date,
 * whether a preview window has opened, whether the fan has access at all —
 * is applied server-side before a playback id is minted, so the id's presence
 * is the whole answer and there is nothing here to re-derive.
 *
 * Skipped by prev/next/shuffle/auto-advance as well as being display-only in
 * the tracklist. The audio engine already refuses to load without a playback
 * id (`use-audio-controller`), so this only stops us offering a play button
 * that could never have worked.
 */
export function isTrackPlayable(track: Track): boolean {
  return !!track.playbackId;
}

/**
 * Shape a post's AUDIO content into a bar-playable Track. `albumId` stays
 * undefined on purpose — it is the bar's album-affordance discriminator
 * (art/title links, album analytics, album token refresh all key on it);
 * `postId` is the post-affordance twin (bar title links to the post).
 */
export function postContentToTrack(content: Content, artworkUrl?: string, postId?: string): Track {
  const seconds = content.duration ?? 0;
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  const mins = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const duration =
    seconds >= 3600
      ? `${Math.floor(seconds / 3600)}:${mins}:${secs}`
      : `${Math.floor(seconds / 60)}:${secs}`;
  return {
    ids: [{ type: 'SINGLE_INTERNAL', id: content.id }],
    title: content.title ?? '',
    order: 0,
    duration,
    description: '',
    lyrics: '',
    explicit: !!content.explicit,
    previewAvailable: true,
    publishingLine: '',
    copyrightLine: '',
    releaseDate: '',
    presaleDate: null,
    parties: [],
    playbackType: 'FULL',
    playbackId: content.playbackId,
    token: content.token ?? null,
    mergedArtists: content.artist ?? '',
    poster: artworkUrl,
    postId,
  };
}

export function getMainArtists(parties: MusicIndustryParty[]): MusicIndustryParty[] {
  return (parties ?? [])
    .filter((p) => p.type === 'ARTIST' && p.subType === 'MAIN')
    .sort((a, b) => a.order - b.order);
}

export function getFeaturedArtists(parties: MusicIndustryParty[]): MusicIndustryParty[] {
  return (parties ?? [])
    .filter((p) => p.type === 'ARTIST' && p.subType === 'FEATURED')
    .sort((a, b) => a.order - b.order);
}

export function getAllArtists(parties: MusicIndustryParty[]): MusicIndustryParty[] {
  return (parties ?? [])
    .filter((p) => p.type === 'ARTIST')
    .sort((a, b) => {
      if (a.subType === 'MAIN' && b.subType !== 'MAIN') return -1;
      if (b.subType === 'MAIN' && a.subType !== 'MAIN') return 1;
      return a.order - b.order;
    });
}

/**
 * Strip a leading ℗ / © (or circled Ⓟ / Ⓒ, or ASCII "(P)" / "(C)") from a delivered
 * P-line / C-line. Distributors differ — UMG ERNs deliver "℗ 2025 UMG Recordings, Inc.",
 * FUGA delivers "Independent 2026" — so renderers strip here and always draw their own
 * symbol in a consistent font (SNGL-7291).
 */
export function stripRightsSymbol(line: string): string {
  return (line ?? '').replace(/^\s*(?:[℗©ⒸⓅ]|\((?:p|c)\))\s*/i, '');
}
